import {
  EMPTY_SYSTEM_TIERED_DISCOUNT,
  ParsedSystemTieredDiscount,
  SystemTieredDiscountConfig,
  SystemTieredDiscountEntry,
  VoucherApiV3,
  VoucherCategoryApiV3,
} from "@/lib/redux/features/voucher/voucherApiV3.types"

const CONDITION_TYPE = "SYSTEM_TIERED_DISCOUNT"

const toNum = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** Chuẩn hoá mã system: bỏ "_" đầu (vd "_9G" → "9G") + viết hoa. */
export const normalizeSystemCode = (code?: string | null): string =>
  String(code ?? "").trim().toUpperCase().replace(/^_+/, "")

const parseEntry = (raw: unknown): SystemTieredDiscountEntry | null => {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>
  const cabinTiers = Array.isArray(r.cabinTiers)
    ? r.cabinTiers
        .map((t) => {
          const tier = (t ?? {}) as Record<string, unknown>
          const match = Array.isArray(tier.match)
            ? tier.match.map((m) => String(m)).filter(Boolean)
            : []
          const rawUnit = String(tier.unitType ?? "FIXED").toUpperCase()
          const unitType = rawUnit === "PERCENT" ? ("PERCENT" as const) : ("FIXED" as const)
          return { match, amount: toNum(tier.amount), unitType }
        })
        .filter((t) => t.match.length > 0)
    : []

  return {
    cabinTiers,
    multiplier: r.multiplier ? String(r.multiplier) : "PER_LEG_AND_PAX",
    domesticFlatAmount: toNum(r.domesticFlatAmount),
    percentOfBasePrice: toNum(r.percentOfBasePrice),
  }
}

/**
 * Quét TẤT CẢ voucher (kể cả voucher IS_HIDDEN — không bị lọc như list hiển thị) trong các category,
 * tìm điều kiện `SYSTEM_TIERED_DISCOUNT` còn hiệu lực và gom cấu hình theo system + mã/tiền tố mở khoá.
 * Không tính toán số tiền ở đây — chỉ trả về cấu hình để `computeSystemTieredDiscount` dùng.
 */
export const parseSystemTieredDiscounts = (
  categories?: VoucherCategoryApiV3[] | null,
): ParsedSystemTieredDiscount => {
  if (!categories?.length) return EMPTY_SYSTEM_TIERED_DISCOUNT

  const systems: SystemTieredDiscountConfig = {}
  const codes = new Set<string>()
  const prefixes = new Set<string>()

  for (const category of categories) {
    for (const voucher of category.vouchers ?? []) {
      const condition = voucher.conditions?.find(
        (c) => c.conditionType === CONDITION_TYPE && c.isEnable !== false,
      )
      if (!condition) continue

      const value = condition.value as { systems?: Record<string, unknown> } | undefined
      const rawSystems = value?.systems
      if (!rawSystems || typeof rawSystems !== "object") continue

      let added = false
      for (const [code, entryRaw] of Object.entries(rawSystems)) {
        const entry = parseEntry(entryRaw)
        if (!entry) continue
        systems[normalizeSystemCode(code)] = entry
        added = true
      }
      if (!added) continue

      collectUnlockKeys(voucher, codes, prefixes)
    }
  }

  if (!Object.keys(systems).length) return EMPTY_SYSTEM_TIERED_DISCOUNT
  return { systems, codes: Array.from(codes), prefixes: Array.from(prefixes) }
}

const collectUnlockKeys = (
  voucher: VoucherApiV3,
  codes: Set<string>,
  prefixes: Set<string>,
) => {
  for (const raw of voucher.codes ?? []) {
    const code = String(raw).trim()
    if (code) codes.add(code)
  }
  const prefix = String(voucher.prefix ?? "").trim()
  if (prefix) prefixes.add(prefix)
}

/** Mã người dùng nhập có thuộc chương trình trừ dần không (khớp mã cụ thể hoặc tiền tố). */
export const isSystemTieredDiscountCode = (
  code: string | null | undefined,
  parsed?: ParsedSystemTieredDiscount | null,
): boolean => {
  const trimmed = (code ?? "").trim()
  if (!trimmed || !parsed) return false
  if (parsed.codes.includes(trimmed)) return true
  return parsed.prefixes.some((p) => p && trimmed.startsWith(p))
}
